import { Admonition } from "#src/shared/components/admonition";

Many libraries can convert a schema into a [JSON Schema](https://json-schema.org/), so it can be consumed by anything that speaks JSON Schema - documentation generators, OpenAPI builders, LLM structured outputs.

We benchmark whichever API the library provides for it, using a small schema with a codec:

```ts
const schema = z.object({
  id: z.number(),
  name: z.string(),
  price: z.codec(z.string(), z.number(), { decode: Number, encode: String }),
});
```

Because of the codec, the **input** and **output** types differ (`price` is a string going in, a number coming out), and so do their JSON schemas.

The **source** column says where the JSON schema comes from - not every library has to do work for it:

- **Runtime** - the library converts the schema when asked, which is what's timed
- **Precompiled** - it's generated at build time, so there's nothing left to do at runtime
- **Already JSON Schema** - the library's schemas are JSON Schema, so there's nothing to convert

The last two are constants, so they're reported as `0` rather than timed - benchmarking a property
read would measure the harness, not the library.

## Standard JSON Schema

Some libraries also implement the [Standard JSON Schema](https://standardschema.dev/json-schema) interface, which lets a tool generate a JSON schema without specialising for each library:

```ts
const jsonSchema = personSchema["~standard"].jsonSchema.input({ target: "draft-2020-12" });
```

The **Standard JSON Schema** column shows how each library supports it:

- **Native** - the library implements it
- **Native Opt in** - implemented, but it has to be enabled first (`S.enableStandardJSONSchema()`)
- **Separate package** - a separate package implements it (`@valibot/to-json-schema`)
- **None** - nothing implements it, so the library's own API is benchmarked

<Admonition type="info" title="Support">

A library is expected to throw for anything it can't convert, so a refused combination has no result
to compare - hover the cross in the matrix to see why the library refused.

</Admonition>

<Admonition type="note" title="Generated schemas">

Every generated schema is checked against the data it describes, but libraries don't agree on what a
JSON schema for the same type looks like - each result links to what its library generated.

</Admonition>
